// src/pages/PVPage.js
import { DashboardPvHtml } from '../../components/skins/dashboard-pv.ts';
import { globalState } from "../../framework/globalState.ts";
import { navigateTo } from '../../framework/router.ts';
import { subscribeRegister } from "../../services/mqttClient.ts";
import { RechartsChartManager } from "../../components/charts/RechartsChartManager.tsx";
import { chartStyles } from "../../components/charts/ChartStyles.ts";
import {
  getPvWarmSnapshot,
  primePvWarmSnapshot,
  startPvWarmPolling,
  subscribePvWarmSnapshot,
  type PvWarmSnapshot,
} from '../../services/pvWarmCache.ts';
import { groupByDictionaryKey, applyThreshold } from '../../services/registerUtils.ts';
import {
  clearThresholdIndicator,
  computeAggregateFromMap,
  getSummaryThresholdSeverity,
  type SummaryThresholdSpec,
} from '../../services/summaryThreshold.ts';

/* ========================================
   MAIN EXPORT: PVPage Component
   ======================================== */
export default function PVPage() {
  const container = document.createElement("div");
  container.classList.add("parent-container");
  container.classList.add("dashboard-pv-page");
  const pvskin = new DashboardPvHtml().pvskin();
  container.innerHTML = pvskin.trim();

  const siteWithData = globalState.currentSiteWithData as any;
  const site = siteWithData?.site || siteWithData;
  const registers = (siteWithData?.devices || []).flatMap((d: any) =>
        ((d.registers || []) as any[]).map((r: any) => ({ ...r, deviceName: d.name, deviceId: d._id }))
      );

  const subscriptions = [];
  const normalizeDictKey = (value: unknown) => String(value || '').trim().toLowerCase();
  let hasShownInitialPvLoading = false;
  let hasClearedInitialPvLoading = false;
  let initialPvRevealTimers: number[] = [];
  let pvWarmUnsubscribe: (() => void) | null = null;

  // Early exit if no site
  if (!site?._id) {
    container.innerHTML += "<p class='text-center text-danger'>No site selected.</p>";
    return container;
  }

  /* ========================================
     DOM ELEMENT CACHE
     ======================================== */
  const cabinTableBody = container.querySelector(".cabinBody");
  const remoteControlSection = container.querySelector('.container-column.remote-control');
  const pvAlarmsBody = container.querySelector('[data-pv-alarms-body]') as HTMLElement | null;
  const summaryElements = {
    pvactivepower: container.querySelector('[data-register="pvactivepower"]'),
    pvdailyenergy: container.querySelector('[data-register="pvdailyenergy"]'),
    pvtotalenergy: container.querySelector('[data-register="pvtotalenergy"]'),
    pvefficiency: container.querySelector('[data-register="pvefficiency"]'),
    dccapacity: container.querySelector('[data-register="dccapacity"]'),
    accapacity: container.querySelector('[data-register="accapacity"]'),
    installedcapacity: container.querySelector('[data-register="installedcapacity"]'),
    inverteravailability: container.querySelector('[data-register="inverteravailability"]'),
    performanceratio: container.querySelector('[data-register="performanceratio"]'),
    specificyield: container.querySelector('[data-register="specificyield"]'),
    totalacpower: container.querySelector('[data-register="totalacpower"]'),
    curtailment: container.querySelector('[data-register="curtailment"]'),
    irradiance: container.querySelector('[data-register="irradiance"]'),
    moduletemperaturekpi: container.querySelector('[data-register="moduletemperaturekpi"]'),
    pvalarms: container.querySelector('[data-register="pvalarms"]'),
  };

  const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const setSummaryText = (key: keyof typeof summaryElements, text: string) => {
    const el = summaryElements[key] as HTMLElement | null;
    if (el) el.textContent = text;
  };

  const setSummaryNumber = (key: keyof typeof summaryElements, value: unknown, decimals = 2) => {
    const n = toFiniteNumber(value);
    setSummaryText(key, n === null ? 'N/A' : n.toFixed(decimals));
  };

  const showInitialPvLoading = () => {
    if (hasShownInitialPvLoading) return;
    const shimmerTargets = [
      ...Array.from(container.querySelectorAll('.dashboard-panel')),
      ...Array.from(container.querySelectorAll('.pv-summary-list > div')),
    ];
    shimmerTargets.forEach((el) => el.classList.add('is-loading'));
    hasShownInitialPvLoading = true;
  };

  const clearInitialPvLoadingProgressively = () => {
    if (hasClearedInitialPvLoading) return;
    hasClearedInitialPvLoading = true;

    initialPvRevealTimers.forEach((timerId) => window.clearTimeout(timerId));
    initialPvRevealTimers = [];

    const revealGroups = [
      Array.from(container.querySelectorAll('.pv-summary-list > div')),
      Array.from(container.querySelectorAll('.dashboard-panel')),
    ];

    revealGroups.forEach((group, index) => {
      const timerId = window.setTimeout(() => {
        group.forEach((el) => el.classList.remove('is-loading'));
      }, index * 120);
      initialPvRevealTimers.push(timerId);
    });
  };

  const formatAlarmTime = (value: unknown) => {
    const date = value ? new Date(String(value)) : null;
    if (!date || Number.isNaN(date.getTime())) return '--:--:--';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  const renderPvAlarmRows = (alarms: any[]) => {
    if (!pvAlarmsBody) return;

    const list = Array.isArray(alarms) ? alarms.slice(0, 6) : [];
    if (!list.length) {
      pvAlarmsBody.innerHTML = '<tr><td colspan="4" class="pv-alarms-empty">No active PV alarms</td></tr>';
      return;
    }

    pvAlarmsBody.innerHTML = list.map((alarm: any) => {
      const severity = String(alarm?.severity || '-').toLowerCase();
        const status = String(alarm?.state || alarm?.status || 'active').toLowerCase();
        const message = String(alarm?.message || alarm?.title || 'Alarm event');
      return `
        <tr class="alarm-row priority-${severity}">
          <td>${formatAlarmTime(alarm?.triggeredAt || alarm?.createdAt || alarm?.updatedAt)}</td>
          <td><span class="pv-alarm-severity severity-${severity}">${severity.toUpperCase()}</span></td>
          <td class="pv-alarm-message">${message}</td>
          <td><span class="pv-alarm-status status-${status}">${status.toUpperCase()}</span></td>
        </tr>
      `;
    }).join('');
  };

  const applyPvWarmSnapshot = (snapshot: PvWarmSnapshot) => {
    const pvSummary: any = snapshot?.pvSummary || null;
    const kpiMetrics: any = snapshot?.kpiMetrics || null;

    const alarmsList = Array.isArray(snapshot?.alarms) ? snapshot.alarms : [];
    setSummaryText('pvalarms', String(alarmsList.length));
    renderPvAlarmRows(alarmsList as any[]);

    if (!pvSummary && !kpiMetrics) {
      clearInitialPvLoadingProgressively();
      return;
    }

    setSummaryNumber('installedcapacity', pvSummary?.installedCapacity, 2);
    setSummaryNumber('totalacpower', pvSummary?.totalAcPower, 2);
    setSummaryNumber('curtailment', pvSummary?.curtailment, 2);
    setSummaryNumber('irradiance', pvSummary?.irradiance, 1);
    setSummaryNumber('moduletemperaturekpi', pvSummary?.moduleTemperature, 1);

    const online = toFiniteNumber(pvSummary?.inverterAvailability?.online);
    const total = toFiniteNumber(pvSummary?.inverterAvailability?.total);
    const percent = toFiniteNumber(pvSummary?.inverterAvailability?.percent);
    if (percent !== null) {
      const suffix = online !== null && total !== null ? ` (${online}/${total})` : '';
      setSummaryText('inverteravailability', `${percent.toFixed(1)}${suffix}`);
    } else {
      setSummaryText('inverteravailability', 'N/A');
    }

    setSummaryNumber('performanceratio', pvSummary?.performanceRatio ?? kpiMetrics?.performanceRatio, 2);
    setSummaryNumber('specificyield', pvSummary?.specificYield ?? kpiMetrics?.specificYield, 2);

    clearInitialPvLoadingProgressively();
  };

  if (!cabinTableBody) {
    console.error("Cabin table body not found.");
    return container;
  }

  /* ========================================
     INVERTER GROUP DETECTION (Config-Driven)
     ======================================== */
  const inverterInstances = new Set<number>();
  registers.forEach((reg: any) => {
    const dk = normalizeDictKey(reg.dictionaryKey);
    if (!['inv_active_power', 'inv_active_power_ac', 'pv_active_power'].includes(dk)) return;
    const instance = Number(reg.instanceNumber);
    inverterInstances.add(Number.isFinite(instance) && instance > 0 ? instance : 1);
  });

  const inverterGroups = Array.from(inverterInstances)
    .sort((a, b) => a - b)
    .map((instanceNumber, index) => ({
      id: `INV ${String(index + 1).padStart(2, "0")}`,
      instanceNumber,
    }));

    /* ========================================
      CABIN TABLE METRICS (dictionaryKey-driven)
      ======================================== */
  const cabinMetrics = [
    'inv_status',
    'inv_energy_total',
    'inv_energy_today',
    'inv_active_power',
    'inv_ac_voltage',
    'inv_ac_current',
    'inv_dc_power',
    'inv_dc_voltage',
    'inv_dc_current',
    'inv_temperature',
  ];

  const cabinDisplayConfig = {};
  cabinMetrics.forEach(configKey => {
    const sample = registers.find(r => normalizeDictKey(r.dictionaryKey) === configKey);
    const displayName = configKey
      .replace(/^inv_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    cabinDisplayConfig[configKey] = {
      key: configKey,
      unit: sample?.unit || '',
      map: configKey === 'inv_status'
        ? { 0: 'Off', 1: 'Running', 2: 'Fault', 3: 'Standby' }
        : null,
      displayName,
    };
  });

  const inverterValues: Record<string, Record<string, number | null>> = {};
  inverterGroups.forEach(inv => {
    inverterValues[inv.id] = {};
  });

  const summaryValues: Record<string, number | null> = {};

  /* ========================================
     CABIN TABLE RENDERING
     ======================================== */
  function updateCabinTable() {
    cabinTableBody.innerHTML = "";
    inverterGroups.forEach(inv => {
      const row = document.createElement("tr");
      const idTd = document.createElement("td");
      idTd.textContent = inv.id;
      row.appendChild(idTd);

      cabinMetrics.forEach(configKey => {
        const cfg = cabinDisplayConfig[configKey];
        if (!cfg) {
          row.appendChild(document.createElement("td"));
          return;
        }

        const match = Object.keys(inverterValues[inv.id]).find((name) => {
          const reg = registers.find(r => r.name === name);
          return normalizeDictKey(reg?.dictionaryKey) === configKey && Number(reg?.instanceNumber || 1) === inv.instanceNumber;
        });

        const rawValue = match ? inverterValues[inv.id][match] : null;
        const displayValue = rawValue != null
          ? (cfg.map ? cfg.map[rawValue] ?? rawValue : Number(rawValue).toFixed(2))
          : "N/A";

        const td = document.createElement("td");
        td.className = "text-right bg-subcontainer w-100";
        td.textContent = displayValue;

        if (rawValue != null && configKey !== 'inv_status') {
          const meta = match ? registers.find(r => r.name === match)?.meta : undefined;
          applyThreshold(td as HTMLElement, Number(rawValue), meta);
        } else if (configKey === 'inv_status') {
          td.style.color = '#ffffff';
          const statusCode = Number(rawValue);
          td.style.color = '#ffffff';
          if (statusCode === 1) {
            td.style.setProperty('background-color', '#2e7d32');
          } else if (statusCode === 0) {
            td.style.setProperty('background-color', '#d32f2f');
          } else if (statusCode === 2) {
            td.style.setProperty('background-color', '#b91c1c');
          } else if (statusCode === 3) {
            td.style.setProperty('background-color', '#f59e0b');
            td.style.color = '#111827';
          } else {
            td.style.removeProperty('background-color');
            td.style.color = '';
          }
          td.textContent = cfg.map?.[statusCode] ?? String(rawValue);
        }

        row.appendChild(td);
      });
      cabinTableBody.appendChild(row);
    });
  }

  // 1. Fill summaryValues & inverterValues with current known values
  registers.forEach(register => {
    const name = register.name;
    const key = register.dictionaryKey;
    if (!key) return;

    const rawValue = register.value ?? null;
    summaryValues[name] = typeof rawValue === 'number' ? rawValue : null;

    // Also store in inverter table if it's an inverter register
    cabinMetrics.forEach(configKey => {
      const cabinCfg = cabinDisplayConfig[configKey];
      if (cabinCfg && normalizeDictKey(key) === configKey) {
        const instanceNumber = Number((register as any).instanceNumber || 1);
        const group = inverterGroups.find(g => g.instanceNumber === (Number.isFinite(instanceNumber) && instanceNumber > 0 ? instanceNumber : 1));
        if (group) {
          inverterValues[group.id][name] = rawValue; // store raw for status mapping
        }
      }
    });
  });

  // 2. IMMEDIATELY UPDATE UI with current values
  requestAnimationFrame(() => {
    updateCabinTable();   // Inverter table
    updateSummary();      // Top summary boxes
  });

  /* ========================================
     SUBSCRIBE TO INVERTER REGISTERS
     ======================================== */
  inverterGroups.forEach(inv => {
    cabinMetrics.forEach(configKey => {
      const cfg = cabinDisplayConfig[configKey];
      if (!cfg) return;

      const register = registers.find(r =>
        normalizeDictKey(r.dictionaryKey) === configKey && Number((r as any).instanceNumber || 1) === inv.instanceNumber
      );
      if (!register) return;

      inverterValues[inv.id][register.name] = register.value ?? null;
      const unsubscribe = subscribeRegister(site._id, register.deviceId, register._id, payload => {
        const p = payload as any;
        const value = typeof p?.value === "number" ? p.value : null;
        inverterValues[inv.id][register.name] = value;
        updateCabinTable();
      });
      subscriptions.push(unsubscribe);
    });
  });

  /* ========================================
     DYNAMIC METRICS TABLE (CONFIG-DRIVEN)
     ======================================== */
  const pvTableMetrics = [
    'inv_active_power',
    'inv_reactive_power',
    'inv_ac_voltage',
    'inv_ac_current',
    'inv_temperature',
    'inv_frequency',
    'inv_availability',
    'inv_dc_capacity',
    'inv_ac_capacity',
  ];

  const metricDisplayInfo = {};
  pvTableMetrics.forEach(configKey => {
      const sample = registers.find(r => normalizeDictKey(r.dictionaryKey) === configKey);
      const displayName = configKey
        .replace(/^inv_/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      metricDisplayInfo[configKey] = {
        label: getLabelForKey(configKey),
        unit: sample?.unit || '',
        displayName,
      };
    });

  function getLabelForKey(key) {
    const labels = {
      pv_active_power: 'P',
      pv_reactive_power: 'Q',
      pv_ac_voltage: 'Voltage',
      pv_ac_current: 'Current',
      pv_temperature: 'Temperature',
      pv_frequency: 'Frequency',
      pv_availability: 'Availability',
      pv_dc_capacity: 'DC Capacity',
      pv_ac_capacity: 'AC Capacity',
      inv_active_power: 'P',
      inv_reactive_power: 'Q',
      inv_ac_voltage: 'Voltage',
      inv_ac_current: 'Current',
      inv_temperature: 'Temperature',
      inv_frequency: 'Frequency',
      inv_availability: 'Availability',
      inv_dc_capacity: 'DC Capacity',
      inv_ac_capacity: 'AC Capacity',
    };
    return labels[key] || key;
  }

  // PV INDEX DETECTION
  const pvIndices = new Set<number>();
  registers.forEach(r => {
    const match = r.name.match(/_(\d+)$/);
    if (match && pvTableMetrics.includes(normalizeDictKey(r.dictionaryKey))) {
      pvIndices.add(Number(match[1]));
    }
  });
  const hasBaseRegister = pvTableMetrics.some(configKey => {
    return registers.some(r => normalizeDictKey(r.dictionaryKey) === configKey && !/(\d+)$/.test(r.name));
  });
  if (hasBaseRegister || pvIndices.size === 0) pvIndices.add(1);
  const pvList = Array.from(pvIndices).sort((a, b) => a - b);

  // Inject Table
  if (remoteControlSection) {
    const table = remoteControlSection.querySelector("table");
    if (table) {
      table.innerHTML = `
        <thead>
          <tr>
            <th colspan="2"></th>
            ${pvList.map(i => `<th>PV ${i}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${pvTableMetrics.map(configKey => {
            const info = metricDisplayInfo[configKey];
            if (!info) return '';
            return `
              <tr data-metric="${info.displayName}">
                <td>${info.label}</td>
                <td class="text-right">${info.unit}</td>
                ${pvList.map(i => `
                  <td class="text-right w-100 bg-subcontainer"
                      data-pv="${i}"
                      data-metric="${info.displayName}">
                    0.0
                  </td>
                `).join('')}
              </tr>
            `;
          }).join('')}
        </tbody>
      `;
    }
  }

  const pvRegisterGroups = groupByDictionaryKey(registers);
  const summaryMapping: Record<string, string[]> = {
    pvactivepower: ['inv_active_power', 'inv_active_power_ac', 'pv_active_power'],
    pvdailyenergy: ['inv_energy_today', 'inv_daily_energy', 'pv_energy_today'],
    pvtotalenergy: ['inv_energy_total', 'inv_total_energy', 'pv_energy_total'],
    pvefficiency: ['inv_frequency', 'pv_frequency'],
    dccapacity: ['inv_dc_capacity', 'pv_dc_capacity'],
    accapacity: ['inv_ac_capacity', 'pv_ac_capacity'],
  };

  const summarySpec: SummaryThresholdSpec = {
    pvactivepower: { keys: ['INV_ACTIVE_POWER', 'INV_ACTIVE_POWER_AC'], mode: 'sum' },
    pvdailyenergy: { keys: ['INV_ENERGY_TODAY', 'INV_DAILY_ENERGY'], mode: 'sum' },
    pvtotalenergy: { keys: ['INV_ENERGY_TOTAL', 'INV_TOTAL_ENERGY'], mode: 'sum' },
    pvefficiency: { keys: ['INV_FREQUENCY'], mode: 'avg' },
    dccapacity: { keys: ['INV_DC_CAPACITY'], mode: 'sum' },
    accapacity: { keys: ['INV_AC_CAPACITY'], mode: 'sum' },
  };

  const summaryValueMode: Record<string, 'sum' | 'avg'> = {
    pvactivepower: 'sum',
    pvdailyenergy: 'sum',
    pvtotalenergy: 'sum',
    pvefficiency: 'avg',
    dccapacity: 'sum',
    accapacity: 'sum',
  };


  function updateSummary() {
    Object.entries(summaryMapping).forEach(([elementKey, configKeys]) => {
      const el = summaryElements[elementKey];
      if (!el) return;

      const group = (configKeys || []).flatMap((k) => pvRegisterGroups[k] || []);
      const value = computeAggregateFromMap(group as any[], summaryValues, summaryValueMode[elementKey] || 'sum');

      if (value !== null) {
        const formatted = value.toFixed(2);
        el.textContent = formatted;
        const severity = getSummaryThresholdSeverity(elementKey, value, registers as any[], summarySpec);
        const target = el as HTMLElement;
        target.style.backgroundColor = '';
        target.style.transition = 'color 0.25s ease';
        if (severity === 'critical') {
          target.style.color = '#ef4444';
          target.title = 'Critical threshold breach';
        } else if (severity === 'high') {
          target.style.color = '#f59e0b';
          target.title = 'Threshold warning';
        } else {
          target.style.color = '#22c55e';
          target.title = 'Within threshold';
        }
      } else {
        el.textContent = "N/A";
        clearThresholdIndicator(el as HTMLElement);
      }
    });
  }

  /* ========================================
     SUBSCRIBE TO ALL PV REGISTERS (Dynamic + Summary)
     ======================================== */
  if (site && registers.length) {
    Object.entries(pvRegisterGroups)
      .filter(([key]) => key.startsWith('inv_') || key.startsWith('met_') || key.startsWith('pv_'))
      .forEach(([configKey, regs]) => {
        regs.forEach(register => {
          summaryValues[register.name] = typeof register.value === 'number' ? register.value : null;
          const unsubscribe = subscribeRegister(site._id, register.deviceId, register._id, payload => {
            const raw = (payload as any)?.value;
            const value = typeof raw === 'number' ? raw : null;
            summaryValues[register.name] = value;

            const match = register.name.match(/_(\d+)$/);
            const pvIndex = match ? Number(match[1]) : 1;
            const displayMetric = configKey
              .replace(/^inv_/, '')
              .replace(/_/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase());

            const cell = container.querySelector(`td[data-pv="${pvIndex}"][data-metric="${displayMetric}"]`) as HTMLElement;
            if (cell && value !== null) {
              cell.textContent = value.toFixed(1);
              applyThreshold(cell, value, register.meta);
            }
            updateSummary();
          });
          subscriptions.push(unsubscribe);
        });
      });
  }

    /* ========================================
     INSTANT INITIAL VALUES FOR DYNAMIC METRICS TABLE
     ======================================== */
  if (remoteControlSection) {
    Object.entries(pvRegisterGroups)
      .filter(([key]) => key.startsWith('inv_') || key.startsWith('met_') || key.startsWith('pv_'))
      .forEach(([configKey, regs]) => {
        const displayMetric = configKey
          .replace(/^inv_/, '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());

        regs.forEach(register => {
          const raw = register.value;
          if (typeof raw !== 'number') return;
          const match = register.name.match(/_(\d+)$/);
          const pvIndex = match ? Number(match[1]) : 1;
          const cell = container.querySelector(`td[data-pv="${pvIndex}"][data-metric="${displayMetric}"]`) as HTMLElement;
          if (cell) {
            cell.textContent = raw.toFixed(1);
            applyThreshold(cell, raw, register.meta);
          }
        });
      });
  }

  setTimeout(() => {
    updateCabinTable();
    updateSummary();
  }, 100);

  const cachedPvSnapshot = getPvWarmSnapshot(site._id);
  if (cachedPvSnapshot) {
    hasShownInitialPvLoading = true;
    hasClearedInitialPvLoading = false;
    applyPvWarmSnapshot(cachedPvSnapshot);
  } else {
    showInitialPvLoading();
    void primePvWarmSnapshot(site._id).then((snapshot) => {
      applyPvWarmSnapshot(snapshot);
    }).catch((error) => {
      console.warn('PV warm snapshot prime failed', error);
      clearInitialPvLoadingProgressively();
    });
  }

  startPvWarmPolling(site._id, 20000);
  pvWarmUnsubscribe = subscribePvWarmSnapshot(site._id, (snapshot) => {
    applyPvWarmSnapshot(snapshot);
  });

  /* ========================================
     CHART INITIALIZATION
     ======================================== */
  const chartManager = new RechartsChartManager(site, registers);

  const setBindingsForPreset = (chartId: string, preset: string) => {
    switch (chartId) {
      case 'pv_power_comparison': {
        if (preset === 'energy') {
          chartManager.bindRegisterToChart(chartId, [
            { assetType: 'pv', metricKey: 'dailyEnergy', name: 'Daily Energy' },
            { assetType: 'pv', metricKey: 'totalEnergy', name: 'Total Energy', line: { color: '#ff8800' } },
          ]);
          return;
        }
        chartManager.bindRegisterToChart(chartId, [
          { assetType: 'pv', metricKey: 'activePower', name: 'Total PV Power' },
          { assetType: 'pv', metricKey: 'reactivePower', name: 'Total PV Reactive Power', line: { color: '#ff8800' } },
        ]);
        return;
      }
      default:
        break;
    }
  };

  let activeFullscreenPanel: HTMLElement | null = null;

  const applyChartFullscreenState = () => {
    const root = container.querySelector('.dashboard-pv-v2') as HTMLElement | null;
    if (!root) return;

    root.classList.toggle('dashboard-chart-focus-active', !!activeFullscreenPanel);
    root.querySelectorAll('.dashboard-panel').forEach((panel) => {
      panel.classList.remove('dashboard-chart-focus');
    });

    container.querySelectorAll('.full-screen-button').forEach((btn) => {
      btn.classList.remove('active');
      (btn as HTMLButtonElement).title = 'Fullscreen';
      (btn as HTMLButtonElement).innerHTML = '<span class="iconify" data-icon="mdi:fullscreen" style="font-size:14px;pointer-events:none;"></span>';
    });

    if (!activeFullscreenPanel) {
      window.dispatchEvent(new Event('resize'));
      return;
    }

    activeFullscreenPanel.classList.add('dashboard-chart-focus');

    const actionButton = activeFullscreenPanel.querySelector('.full-screen-button') as HTMLButtonElement | null;
    if (actionButton) {
      actionButton.classList.add('active');
      actionButton.title = 'Exit Fullscreen';
      actionButton.innerHTML = '<span class="iconify" data-icon="mdi:fullscreen-exit" style="font-size:14px;pointer-events:none;"></span>';
    }

    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  };

  const setChartFullscreen = (panel: HTMLElement) => {
    activeFullscreenPanel = activeFullscreenPanel === panel ? null : panel;
    applyChartFullscreenState();
  };

  const onDashboardKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !activeFullscreenPanel) return;
    activeFullscreenPanel = null;
    applyChartFullscreenState();
  };

  const wireDashboardControls = () => {
    const setPvSummaryTab = (tab: string) => {
      container.querySelectorAll('[data-chart-control="pv-summary-tab"]').forEach((chip) => {
        chip.classList.toggle('active', (chip as HTMLElement).dataset.tab === tab);
      });

      container.querySelectorAll('[data-pv-summary-item]').forEach((item) => {
        const isMatch = (item as HTMLElement).dataset.tab === tab;
        (item as HTMLElement).style.display = isMatch ? '' : 'none';
      });
    };

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const fullscreenButton = target.closest('.full-screen-button') as HTMLButtonElement | null;
      if (fullscreenButton) {
        const panel = fullscreenButton.closest('.dashboard-panel') as HTMLElement | null;
        if (!panel) return;
        setChartFullscreen(panel);
        return;
      }

      const button = target.closest('.dashboard-v2-chart-actions .chip') as HTMLButtonElement | null;
      if (!button) return;

      const control = button.dataset.chartControl;
      if (control === 'pv-summary-tab') {
        setPvSummaryTab(button.dataset.tab || 'core');
        return;
      }

      if (button.dataset.action === 'view-pv-alarms') {
        navigateTo('/dashboard/alarms');
        return;
      }

      if (control !== 'preset') return;

      const actions = button.parentElement;
      if (actions) {
        actions.querySelectorAll('.chip[data-chart-control="preset"]').forEach((chip) => chip.classList.remove('active'));
      }
      button.classList.add('active');

      setBindingsForPreset(button.dataset.chartId || '', button.dataset.preset || 'power');
    });

    container.addEventListener('change', (event) => {
      const target = event.target as HTMLSelectElement | null;
      if (!target) return;

      const control = target.dataset.chartControl;
      if (control !== 'type') return;

      chartManager.setChartType(target.dataset.chartId || '', target.value);
    });

    setPvSummaryTab('core');
  };

  chartManager.createChart("pv_power_comparison", [], {
    ...chartStyles.pvPowerLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  setBindingsForPreset('pv_power_comparison', 'power');

  chartManager.createChart("pv_specific_power_comparison", [], {
    ...chartStyles.pvSpecificLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  chartManager.bindRegisterToChart("pv_specific_power_comparison", [
    { assetType: "pv", metricKey: "activePower", name: "PV Active Power" }
  ]);

  chartManager.createChart("module_temp_comparison", [], {
    ...chartStyles.averageTempLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  chartManager.bindRegisterToChart("module_temp_comparison", [
    { assetType: "pv", metricKey: "moduleTemperature", name: "Avg. Module Temperature" }
  ]);

  chartManager.createChart("pv_availability_comparison", [], {
    ...chartStyles.pvAvailabilityLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  chartManager.bindRegisterToChart("pv_availability_comparison", [
    { assetType: "pv", metricKey: "availability", name: "Avg. PV Availability" }
  ]);

  /* ========================================
     INITIALIZATION & CLEANUP
     ======================================== */
  wireDashboardControls();
  document.addEventListener('keydown', onDashboardKeydown);

  // normalize fullscreen icons to match dashboard style
  container.querySelectorAll('.full-screen-button').forEach((btn) => {
    (btn as HTMLButtonElement).innerHTML = '<span class="iconify" data-icon="mdi:fullscreen" style="font-size:14px;pointer-events:none;"></span>';
    (btn as HTMLButtonElement).title = 'Fullscreen';
  });

  (container as any).cleanup = () => {
    document.removeEventListener('keydown', onDashboardKeydown);
    if (typeof pvWarmUnsubscribe === 'function') {
      pvWarmUnsubscribe();
      pvWarmUnsubscribe = null;
    }
    initialPvRevealTimers.forEach((timerId) => window.clearTimeout(timerId));
    subscriptions.forEach(unsub => typeof unsub === "function" && unsub());
    chartManager.cleanup();
  };

  return container;
}